import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  LayoutDashboard, Receipt, Users, Wallet, FileText, Settings, LogOut, Menu, X, 
  Home, Target, Bell, AlertCircle, Calendar, ClipboardList, ChevronDown, 
  UserCog, Shield, Building2, CreditCard, MoreHorizontal, MessageCircle, Search,
  User, Phone, MapPin, IndianRupee, ArrowRight, Network, FileInput, Globe, AlertTriangle, UserCheck, TrendingUp
} from 'lucide-react';
import api from '@/utils/api';
import VoiceAssistant from './VoiceAssistant';
import AssistantChat from './AssistantChat';

// Role-based permissions
const ROLE_PERMISSIONS = {
  admin: ['dashboard', 'properties', 'billing', 'customers', 'leads', 'expenses', 'reports', 'forecast', 'settings', 'plans', 'users', 'roles', 'whatsapp', 'partner-network', 'reminders', 'smart-import', 'website', 'salary', 'agents', 'activity-logs'],
  sales: ['dashboard', 'leads', 'customers', 'billing', 'plans', 'settings', 'smart-import', 'agents'],
  billing: ['dashboard', 'customers', 'billing', 'forecast', 'plans', 'settings', 'smart-import', 'agents'],
  customer: ['customer_portal']
};

export const Layout = ({ children, user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileMore, setShowMobileMore] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Universal Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ customers: [], bookings: [] });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);

  // Get user permissions
  const userPermissions = ROLE_PERMISSIONS[user?.role] || [];
  const hasPermission = (permission) => userPermissions.includes(permission) || user?.role === 'admin';

  // Handle click outside search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Universal search function
  const handleUniversalSearch = async (query) => {
    setSearchQuery(query);
    
    if (query.length < 2) {
      setSearchResults({ customers: [], bookings: [] });
      setShowSearchResults(false);
      return;
    }

    setSearchLoading(true);
    setShowSearchResults(true);

    try {
      const [customersRes, bookingsRes] = await Promise.all([
        api.get('/simple-customers'),
        api.get('/bookings')
      ]);

      const queryLower = query.toLowerCase();

      // Filter customers
      const filteredCustomers = customersRes.data.filter(c =>
        c.customer_name.toLowerCase().includes(queryLower) ||
        c.mobile_number.includes(query)
      ).slice(0, 5);

      // Filter bookings
      const filteredBookings = bookingsRes.data.filter(b =>
        b.customer_name.toLowerCase().includes(queryLower) ||
        b.booking_number.toLowerCase().includes(queryLower) ||
        b.property_name.toLowerCase().includes(queryLower) ||
        b.mobile_number.includes(query)
      ).slice(0, 5);

      setSearchResults({
        customers: filteredCustomers,
        bookings: filteredBookings
      });
    } catch (error) {
      console.error('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchResultClick = (type, id) => {
    setShowSearchResults(false);
    setSearchQuery('');
    
    if (type === 'customer') {
      navigate('/admin/customers');
    } else if (type === 'booking') {
      navigate(`/admin/billing?view=${id}`);
    }
  };

  // Fetch due installments for notifications
  useEffect(() => {
    const fetchDueInstallments = async () => {
      try {
        const response = await api.get('/bookings');
        const bookings = response.data;
        const today = new Date();
        const dueNotifications = [];

        bookings.forEach(booking => {
          if (booking.installments && booking.payment_type === 'Installment') {
            booking.installments.forEach(inst => {
              if (inst.status === 'Pending') {
                const dueDate = new Date(inst.due_date);
                const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                
                if (diffDays <= 7) {
                  dueNotifications.push({
                    id: `${booking.id}-${inst.installment_name}`,
                    bookingId: booking.id,
                    bookingNumber: booking.booking_number,
                    customerName: booking.customer_name,
                    installmentName: inst.installment_name,
                    amount: inst.amount,
                    dueDate: inst.due_date,
                    diffDays: diffDays,
                    isOverdue: diffDays < 0
                  });
                }
              }
            });
          }
        });

        dueNotifications.sort((a, b) => a.diffDays - b.diffDays);
        setNotifications(dueNotifications);
      } catch (error) {
        console.error('Failed to fetch notifications');
      }
    };

    if (user && user.role !== 'customer') {
      fetchDueInstallments();
      const interval = setInterval(fetchDueInstallments, 300000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleNotificationClick = () => {
    setShowNotifications(false);
    navigate('/admin/billing?tab=reminders');
  };

  // Grouped navigation structure
  const navGroups = [
    {
      id: 'main',
      items: [
        { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard', permission: 'dashboard' },
      ]
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: CreditCard,
      permission: 'billing',
      items: [
        { path: '/admin/billing', icon: Receipt, label: 'Bookings', testId: 'nav-billing', permission: 'billing' },
        { path: '/admin/red-alerts', icon: AlertTriangle, label: 'Red Alerts', testId: 'nav-red-alerts', permission: 'billing' },
        { path: '/admin/plans', icon: ClipboardList, label: 'Plans', testId: 'nav-plans', permission: 'plans' },
        { path: '/admin/smart-import', icon: FileInput, label: 'Smart Import', testId: 'nav-smart-import', permission: 'smart-import' },
      ]
    },
    {
      id: 'crm',
      label: 'CRM',
      icon: Users,
      permission: 'customers',
      items: [
        { path: '/admin/customers', icon: Users, label: 'Customers', testId: 'nav-customers', permission: 'customers' },
        { path: '/admin/leads', icon: Target, label: 'Leads', testId: 'nav-leads', permission: 'leads' },
      ]
    },
    {
      id: 'property',
      items: [
        { path: '/admin/properties', icon: Building2, label: 'Properties', testId: 'nav-properties', permission: 'properties' },
        { path: '/admin/agents', icon: UserCheck, label: 'Sales Agents', testId: 'nav-agents', permission: 'agents' },
      ]
    },
    {
      id: 'finance',
      items: [
        { path: '/admin/expenses', icon: Wallet, label: 'Expenses', testId: 'nav-expenses', permission: 'expenses' },
        { path: '/admin/salary', icon: IndianRupee, label: 'Salary', testId: 'nav-salary', permission: 'salary' },
        { path: '/admin/reports', icon: FileText, label: 'Reports', testId: 'nav-reports', permission: 'reports' },
        { path: '/admin/forecast', icon: TrendingUp, label: 'Revenue Forecast', testId: 'nav-forecast', permission: 'forecast' },
      ]
    },
    {
      id: 'partner',
      label: 'Partner Network',
      icon: Network,
      permission: 'partner-network',
      items: [
        { path: '/admin/partner-network', icon: Network, label: 'Ganesa Partners', testId: 'nav-partner-network', permission: 'partner-network' },
      ]
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: Shield,
      permission: 'users',
      items: [
        { path: '/admin/users', icon: UserCog, label: 'Users', testId: 'nav-users', permission: 'users' },
        { path: '/admin/roles', icon: Shield, label: 'Roles', testId: 'nav-roles', permission: 'roles' },
        { path: '/admin/whatsapp', icon: MessageCircle, label: 'WhatsApp', testId: 'nav-whatsapp', permission: 'whatsapp' },
        { path: '/admin/reminders', icon: Bell, label: 'Reminders', testId: 'nav-reminders', permission: 'reminders' },
        { path: '/admin/website', icon: Globe, label: 'Website', testId: 'nav-website', permission: 'website' },
        { path: '/admin/settings', icon: Settings, label: 'Settings', testId: 'nav-settings', permission: 'settings' },
        { path: '/admin/activity-logs', icon: FileText, label: 'Activity Logs', testId: 'nav-activity-logs', permission: 'activity-logs' },
      ]
    },
  ];

  // Filter groups and items based on permissions
  const filteredNavGroups = navGroups.map(group => {
    const filteredItems = group.items.filter(item => hasPermission(item.permission));
    return { ...group, items: filteredItems };
  }).filter(group => group.items.length > 0);

  // Mobile bottom nav items (first 4 important ones)
  const mobileNavItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Home', permission: 'dashboard' },
    { path: '/admin/billing', icon: Receipt, label: 'Billing', permission: 'billing' },
    { path: '/admin/customers', icon: Users, label: 'CRM', permission: 'customers' },
    { path: '/admin/properties', icon: Building2, label: 'Property', permission: 'properties' },
    { path: '/admin/agents', icon: UserCheck, label: 'Sales Agents', permission: 'agents' },
  ].filter(item => hasPermission(item.permission));

  // More menu items for mobile
  const moreMenuItems = [
    { path: '/admin/red-alerts', icon: AlertTriangle, label: 'Red Alerts', permission: 'billing' },
    { path: '/admin/leads', icon: Target, label: 'Leads', permission: 'leads' },
    { path: '/admin/plans', icon: ClipboardList, label: 'Plans', permission: 'plans' },
    { path: '/admin/expenses', icon: Wallet, label: 'Expenses', permission: 'expenses' },
    { path: '/admin/salary', icon: IndianRupee, label: 'Salary', permission: 'salary' },
    { path: '/admin/reports', icon: FileText, label: 'Reports', permission: 'reports' },
    { path: '/admin/forecast', icon: TrendingUp, label: 'Revenue Forecast', permission: 'forecast' },
    { path: '/admin/users', icon: UserCog, label: 'Users', permission: 'users' },
    { path: '/admin/settings', icon: Settings, label: 'Settings', permission: 'settings' },
    { path: '/admin/activity-logs', icon: FileText, label: 'Activity Logs', permission: 'activity-logs' },
  ].filter(item => hasPermission(item.permission));

  const toggleGroup = (groupId) => {
    setExpandedGroup(expandedGroup === groupId ? null : groupId);
  };

  const isGroupActive = (group) => {
    return group.items.some(item => location.pathname === item.path);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20 lg:pb-0">
      {/* Desktop Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 shadow-sm z-40 transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {/* Logo Section */}
        <div className="h-16 flex items-center gap-3 border-b border-slate-200 px-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_proptech-admin-suite/artifacts/jcoqxxo3_logo%20%281%29.png" 
            alt="Ganesa" 
            className="w-10 h-10 object-contain"
          />
          <div>
            <h1 className="font-bold text-slate-800 text-sm">Ganesa</h1>
            <p className="text-xs text-emerald-600 font-medium">Homestate</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-140px)]">
          {filteredNavGroups.map((group) => {
            if (!group.label) {
              return group.items.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={item.testId}
                    className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all text-sm ${
                      isActive
                        ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-lg shadow-emerald-500/20'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-emerald-600'
                    }`}
                  >
                    <item.icon size={18} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              });
            }

            const isActive = isGroupActive(group);
            const isExpanded = expandedGroup === group.id || isActive;

            return (
              <div key={group.id} className="space-y-1">
                <button
                  onClick={() => toggleGroup(group.id)}
                  data-testid={`nav-group-${group.id}`}
                  className={`flex items-center justify-between w-full px-3 py-2.5 rounded-xl transition-all text-sm ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <group.icon size={18} />
                    <span className="font-medium">{group.label}</span>
                  </div>
                  <ChevronDown 
                    size={16} 
                    className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {isExpanded && (
                  <div className="ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
                    {group.items.map((item) => {
                      const isItemActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={item.testId}
                          className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg transition-all text-sm ${
                            isItemActive
                              ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-md'
                              : 'text-slate-500 hover:bg-slate-100 hover:text-emerald-600'
                          }`}
                        >
                          <item.icon size={16} />
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-3 mb-2 px-2">
            <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-sm">
                {user?.full_name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{user?.full_name}</p>
              <p className="text-xs text-emerald-600 capitalize font-medium">{user?.role}</p>
            </div>
          </div>
          <Button
            onClick={onLogout}
            data-testid="logout-button"
            variant="outline"
            className="w-full h-9 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 gap-2 rounded-xl"
          >
            <LogOut size={16} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Header */}
        <header className="h-14 lg:h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_proptech-admin-suite/artifacts/jcoqxxo3_logo%20%281%29.png" 
                alt="Ganesa" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="font-bold text-slate-800 text-base leading-none">Ganesa</h1>
                <p className="text-xs text-emerald-600 font-medium">Homestate</p>
              </div>
            </div>
            
            {/* Desktop Title */}
            <h1 className="hidden lg:block text-lg font-semibold text-slate-800 capitalize">
              {location.pathname.split('/').pop() || 'Dashboard'}
            </h1>
          </div>

          <div className="flex items-center gap-2 lg:gap-4">
            {/* Universal Search Bar */}
            {user?.role !== 'customer' && (
              <div ref={searchRef} className="relative">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    data-testid="universal-search-input"
                    value={searchQuery}
                    onChange={(e) => handleUniversalSearch(e.target.value)}
                    onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                    placeholder="Search customer, booking..."
                    className="w-40 sm:w-48 lg:w-72 h-10 pl-9 pr-4 rounded-xl border border-slate-200 bg-white/80 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm transition-all"
                  />
                </div>

                {/* Search Results Dropdown */}
                {showSearchResults && (
                  <div className="absolute right-0 lg:left-0 top-full mt-2 w-80 lg:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 max-h-[70vh] overflow-y-auto">
                    {searchLoading ? (
                      <div className="p-6 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto"></div>
                        <p className="text-sm text-slate-500 mt-2">Searching...</p>
                      </div>
                    ) : (
                      <>
                        {/* Customers Section */}
                        {searchResults.customers.length > 0 && (
                          <div className="p-3 border-b border-slate-100">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                                <Users size={12} />
                                Customers
                              </p>
                              <Link 
                                to="/admin/customers" 
                                onClick={() => setShowSearchResults(false)}
                                className="text-xs text-emerald-600 hover:underline"
                              >
                                View All
                              </Link>
                            </div>
                            <div className="space-y-1">
                              {searchResults.customers.map((customer) => (
                                <button
                                  key={customer.id}
                                  onClick={() => handleSearchResultClick('customer', customer.id)}
                                  className="w-full p-3 text-left hover:bg-emerald-50 rounded-xl transition-colors group"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                      <User size={18} className="text-emerald-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold text-slate-800 truncate">{customer.customer_name}</p>
                                      <p className="text-xs text-slate-500 flex items-center gap-1">
                                        <Phone size={10} /> {customer.mobile_number}
                                      </p>
                                    </div>
                                    <ArrowRight size={16} className="text-slate-300 group-hover:text-emerald-600 transition-colors" />
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Bookings Section */}
                        {searchResults.bookings.length > 0 && (
                          <div className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                                <Receipt size={12} />
                                Bookings
                              </p>
                              <Link 
                                to="/admin/billing" 
                                onClick={() => setShowSearchResults(false)}
                                className="text-xs text-emerald-600 hover:underline"
                              >
                                View All
                              </Link>
                            </div>
                            <div className="space-y-1">
                              {searchResults.bookings.map((booking) => (
                                <button
                                  key={booking.id}
                                  onClick={() => handleSearchResultClick('booking', booking.id)}
                                  className="w-full p-3 text-left hover:bg-blue-50 rounded-xl transition-colors group"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                      <Receipt size={18} className="text-blue-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p className="font-mono text-xs font-semibold text-blue-600">{booking.booking_number}</p>
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                                          booking.payment_status === 'Fully Paid' 
                                            ? 'bg-green-100 text-green-700' 
                                            : 'bg-amber-100 text-amber-700'
                                        }`}>
                                          {booking.payment_status}
                                        </span>
                                      </div>
                                      <p className="text-sm font-medium text-slate-800 truncate">{booking.customer_name}</p>
                                      <p className="text-xs text-slate-500 truncate">{booking.property_name}</p>
                                      <p className="text-xs font-semibold text-emerald-600 mt-0.5">
                                        ₹{booking.total_price.toLocaleString('en-IN')}
                                        {booking.balance_due > 0 && (
                                          <span className="text-red-500 ml-2">
                                            (Due: ₹{booking.balance_due.toLocaleString('en-IN')})
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                    <ArrowRight size={16} className="text-slate-300 group-hover:text-blue-600 transition-colors" />
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* No Results */}
                        {searchResults.customers.length === 0 && searchResults.bookings.length === 0 && searchQuery.length >= 2 && (
                          <div className="p-6 text-center">
                            <Search size={32} className="mx-auto text-slate-300 mb-2" />
                            <p className="text-slate-500 text-sm">No results found for "{searchQuery}"</p>
                            <p className="text-slate-400 text-xs mt-1">Try searching by name, mobile, or booking number</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Notification Bell */}
            {user?.role !== 'customer' && (
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  data-testid="notification-bell"
                  className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <Bell size={20} className="text-slate-600" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg">
                      {notifications.length > 9 ? '9+' : notifications.length}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                      <h3 className="font-semibold text-slate-800">Reminders</h3>
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">{notifications.length} pending</span>
                    </div>
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-slate-500 text-sm">
                        <Bell size={32} className="mx-auto mb-2 opacity-30" />
                        No upcoming dues
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {notifications.slice(0, 5).map((notif) => (
                          <button
                            key={notif.id}
                            onClick={handleNotificationClick}
                            className="w-full p-3 text-left hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-xl ${notif.isOverdue ? 'bg-red-100' : 'bg-amber-100'}`}>
                                {notif.isOverdue ? (
                                  <AlertCircle size={16} className="text-red-600" />
                                ) : (
                                  <Calendar size={16} className="text-amber-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">{notif.customerName}</p>
                                <p className="text-xs text-slate-500">₹{notif.amount.toLocaleString('en-IN')}</p>
                                <p className={`text-xs mt-0.5 font-medium ${notif.isOverdue ? 'text-red-600' : 'text-amber-600'}`}>
                                  {notif.isOverdue ? `${Math.abs(notif.diffDays)}d overdue` : `Due in ${notif.diffDays}d`}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="p-2 border-t border-slate-100">
                      <Link
                        to="/admin/billing?tab=reminders"
                        onClick={() => setShowNotifications(false)}
                        className="block w-full text-center text-sm text-emerald-600 font-medium py-2 hover:bg-emerald-50 rounded-xl transition-colors"
                      >
                        View All
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Desktop User Avatar */}
            <div className="hidden lg:flex items-center gap-2 pl-2 border-l border-slate-200 ml-2">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">{user?.full_name?.charAt(0) || 'U'}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation - Premium Compact */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-100 z-40 safe-area-inset-bottom shadow-lg">
        <div className="flex items-center justify-around py-1.5 px-1">
          {mobileNavItems.slice(0, 4).map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-all ${
                  isActive
                    ? 'text-emerald-600'
                    : 'text-slate-400'
                }`}
              >
                <div className={`p-1.5 rounded-lg transition-all ${isActive ? 'bg-emerald-100' : ''}`}>
                  <item.icon size={18} />
                </div>
                <span className="text-[9px] font-medium">{item.label}</span>
              </Link>
            );
          })}
          
          {/* More Menu Button */}
          {moreMenuItems.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowMobileMore(!showMobileMore)}
                className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-all ${
                  showMobileMore ? 'text-emerald-600' : 'text-slate-400'
                }`}
              >
                <div className={`p-1.5 rounded-lg transition-all ${showMobileMore ? 'bg-emerald-100' : ''}`}>
                  <MoreHorizontal size={18} />
                </div>
                <span className="text-[9px] font-medium">More</span>
              </button>

              {/* More Menu Popup */}
              {showMobileMore && (
                <>
                  <div 
                    className="fixed inset-0 z-40"
                    onClick={() => setShowMobileMore(false)}
                  />
                  <div className="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
                    {moreMenuItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setShowMobileMore(false)}
                          className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                            isActive
                              ? 'bg-emerald-50 text-emerald-600'
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <item.icon size={18} />
                          <span className="text-sm font-medium">{item.label}</span>
                        </Link>
                      );
                    })}
                    <div className="border-t border-slate-100">
                      <button
                        onClick={() => { setShowMobileMore(false); onLogout(); }}
                        className="flex items-center gap-3 px-4 py-3 w-full text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut size={18} />
                        <span className="text-sm font-medium">Logout</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </nav>
      
      {/* Voice Assistant - Only on Expenses page */}
      {location.pathname.includes('/expenses') && (
        <VoiceAssistant pageContext="expenses" />
      )}

      {/* AI Assistant Chat Bubble */}
      <AssistantChat user={user} />
    </div>
  );
};
